import { format } from 'date-fns';
import { useMemo } from 'react';
import { SimpleDatePicker } from '@/components/DatePicker/SimpleDatePicker';
import { HStack } from '@/components/Layout';
import { useFormatDate } from '@/hooks/date';
import { useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type Props = CFormItemProps & {
  value?: Date | string;
  onChange?: (value: string) => void;
  inputWidth?: number | string;
  inputClassName?: string;
  allowEmpty?: boolean;
};
function parseDateTime(value: Date | string | undefined): {
  date: Date | undefined;
  time: string;
} {
  if (!value) return {
    date: undefined,
    time: ''
  };
  const dateObj = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return {
    date: undefined,
    time: ''
  };
  return {
    date: dateObj,
    time: format(dateObj, 'HH:mm')
  };
}
function combineDateTime(date: Date | undefined, time: string): string {
  if (!date) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const combined = new Date(date);
  if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
    combined.setHours(hours, minutes, 0, 0);
  } else {
    combined.setHours(0, 0, 0, 0);
  }
  return format(combined, "yyyy-MM-dd'T'HH:mm:ss");
}

/**
 * Card-based Form / DateTimeItem (DateTime string with time selection)
 */
export const CFormDateTimeItem = buildCFormItem<Props>((props: Props) => {
  const formatDate = useFormatDate();
  const {
    i18n
  } = useTranslation();
  const {
    date,
    time
  } = useMemo(() => parseDateTime(props.value), [props.value]);
  const displayValue = useMemo(() => {
    if (!props.value) return '';
    const dateObj = props.value instanceof Date ? props.value : new Date(props.value);
    if (Number.isNaN(dateObj.getTime())) return '';
    return `${formatDate(dateObj)} ${format(dateObj, 'HH:mm')}`;
  }, [props.value, formatDate]);
  const handleDateChange = (newDate: Date | undefined) => {
    if (!newDate) {
      props.onChange?.('');
      return;
    }
    props.onChange?.(combineDateTime(newDate, time || '00:00'));
  };
  return <CFormItemBase {...props} key={`datetime-${i18n.language}`} readComponent={displayValue} writeComponent={<HStack className="gap-2">
          <SimpleDatePicker value={date} width={props.inputWidth ?? 180} allowEmpty={props.allowEmpty} onChange={handleDateChange} className={props.inputClassName ?? 'w-full'} disabled={props.readonly} />
        </HStack>} />;
});